// UnassignedNewSocket.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import Navbar from "../NavBar";
import NewSidebar from "../newSideBar";
import UserDetails from "./UserDetails";
import InboxPanel from "./InboxPanel";
import "./inbox.css";
import { io } from "socket.io-client";
import debounce from "lodash.debounce";

// Constants
const CHAT_STATUS = { UNASSIGNED: 1, MY_OPEN: 2 };
const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

// ========== Enhanced useChatData with Unread Counts ==========
const useChatData = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const localURL = localStorage.getItem("shopURL");

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    "x-shop-url": localURL,
  }), [localURL]);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/email-list/${CHAT_STATUS.UNASSIGNED}`,
        { headers }
      );
      const users = res.data.users || [];
      setCollections(users);

      // Fetch unread counts for each user
      const unreadPromises = users.map(async (user) => {
        try {
          const unreadRes = await axios.get(
            `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/unread-count/${user.email}`,
            { headers }
          );
          return { email: user.email, count: unreadRes.data.count || 0 };
        } catch (err) {
          return { email: user.email, count: 0 };
        }
      });
      
      const unreadResults = await Promise.all(unreadPromises);
      const unreadMap = unreadResults.reduce((acc, item) => {
        acc[item.email] = item.count;
        return acc;
      }, {});

      console.log("📊 Unread counts:", unreadMap);
      setUnreadCounts(unreadMap);
    } catch (err) {
      console.error("Error fetching collections:", err);
      setCollections([]);
      setUnreadCounts({});
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const updateUnreadCount = useCallback((email, count) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [email]: count,
    }));
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return { 
    collections, 
    loading, 
    unreadCounts, 
    updateUnreadCount,
    refetch: fetchCollections 
  };
};

// ========== Enhanced Scroll Management ==========
const useScrollManagement = () => {
  const chatContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
  }, []);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: behavior,
      });
      setIsAtBottom(true);
    }
  }, []);

  const scrollToBottomIfNeeded = useCallback(() => {
    if (isAtBottom && chatContainerRef.current) {
      scrollToBottom("smooth");
    }
  }, [isAtBottom, scrollToBottom]);

  return { 
    chatContainerRef, 
    handleScroll, 
    scrollToBottom,
    scrollToBottomIfNeeded,
    isAtBottom 
  };
};

// ========== Enhanced Chat Messages Hook ==========
const useChatMessages = (
  socket,
  selectedCollection,
  updateUnreadCount,
  scrollToBottomIfNeeded
) => {
  const [messages, setMessages] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  // Scroll when typing starts
  useEffect(() => {
    if (isTyping) {
      setTimeout(() => {
        scrollToBottomIfNeeded();
      }, 100);
    }
  }, [isTyping, scrollToBottomIfNeeded]);

  useEffect(() => {
    if (!socket || !selectedCollection) {
      console.log("❌ useChatMessages - Missing socket or selectedCollection");
      setMessagesLoading(false);
      return;
    }

    console.log("🔄 useChatMessages - Setting up for:", selectedCollection);

    const handleLoadMessages = (payload) => {
      console.log("📨 handleLoadMessages received:", payload);
      if (payload?.email === selectedCollection) {
        console.log("✅ Messages loaded for:", selectedCollection);
        // Ensure each message has read status
        const messagesWithReadStatus = (payload.messages || []).map(msg => ({
          ...msg,
          read: msg.read || msg.raw?.read || false
        }));
        
        setMessages(messagesWithReadStatus);
        setUserDetails(payload.userInfo || {});
        setMessagesLoading(false);

        // ✅ Mark messages as read when loaded
        socket.emit("markAsRead", {
          email: selectedCollection,
          role: "admin",
        });

        setTimeout(() => {
          scrollToBottomIfNeeded();
        }, 100);
      }
    };

    const handleReceiveMessage = (payload) => {
      if (payload?.email === selectedCollection) {
        // New messages should start as unread
        const newMessage = {
          _id: payload.raw?._id || payload.time,
          text: payload.message,
          admin: payload.admin,
          time: payload.time,
          read: payload.read || payload.raw?.read || false,
          raw: payload.raw
        };
        
        setMessages((prev) => [...prev, newMessage]);

        // ✅ Auto mark as read when new message received in open chat
        socket.emit("markAsRead", {
          email: selectedCollection,
          role: "admin",
        });

        setTimeout(() => {
          scrollToBottomIfNeeded();
        }, 100);
      } else {
        // Update unread count for other conversations
        if (payload?.email && updateUnreadCount) {
          updateUnreadCount(payload.email, (prev) => (prev || 0) + 1);
        }
      }
    };

    const handleTypingStatus = ({ email, role, isTyping: typing }) => {
      if (email === selectedCollection && role === "user") {
        setIsTyping(typing);
        
        // Scroll to bottom when user starts typing
        if (typing) {
          setTimeout(() => {
            scrollToBottomIfNeeded();
          }, 100);
        }
      }
    };

    const handleMessageSeen = (data) => {
      console.log("👁️ Message seen update:", data);
      // Update the specific message's read status
      setMessages((prev) =>
        prev.map((msg) =>
          (msg._id === data.messageId || msg.time === data.time) 
            ? { ...msg, read: true } 
            : msg
        )
      );
    };

    const handleAllMessagesSeen = (data) => {
      console.log("✅ All messages read by:", data.role);
      if (data.role === "admin") {
        // Update unread count when admin reads messages
        updateUnreadCount(data.email, 0);
      }
    };

    // Load messages
    setMessagesLoading(true);
    setMessages([]); // Clear previous messages
    socket.emit("joinConversation", { email: selectedCollection });

    socket.on("loadMessages", handleLoadMessages);
    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("typingStatus", handleTypingStatus);
    socket.on("messageSeen", handleMessageSeen);
    socket.on("messagesRead", handleAllMessagesSeen);

    const timeoutId = setTimeout(() => {
      console.log("⏰ Loading timeout");
      setMessagesLoading(false);
    }, 10000);

    return () => {
      socket.off("loadMessages", handleLoadMessages);
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("typingStatus", handleTypingStatus);
      socket.off("messageSeen", handleMessageSeen);
      socket.off("messagesRead", handleAllMessagesSeen);
      clearTimeout(timeoutId);
      setIsTyping(false); // Reset typing when leaving conversation
    };
  }, [socket, selectedCollection, updateUnreadCount]);

  return {
    messages,
    userDetails,
    messagesLoading,
    setMessagesLoading,
    isTyping,
  };
};

// --- Utility functions ---
const formatDateOnly = (dateString) => {
  if (!dateString) return "";
  const [datePart] = dateString.split(" ");
  const [day, month, year] = datePart.split("-").map(Number);
  return `${day.toString().padStart(2, "0")}-${month
    .toString()
    .padStart(2, "0")}-${year}`;
};

const formatTimeOnly = (timestamp) => {
  if (!timestamp) return "";
  const parts = timestamp.split(" ");
  if (parts.length < 3) return timestamp;
  const timeParts = parts[1].split(":");
  return `${timeParts[0]}:${timeParts[1]} ${parts[2]}`;
};

const isUserActive = (user) => {
  if (!user?.updatedAt) return false;
  return (
    new Date(user.updatedAt).getTime() >= Date.now() - INACTIVITY_THRESHOLD
  );
};

// ========== Enhanced UnassignedUsersList with Unread Counts ==========
const UnassignedUsersList = ({
  collections,
  tenantName,
  onSelect,
  selectedEmail,
  loadingCollection,
  unreadCounts,
}) => {
  if (!collections || collections.length === 0) {
    return (
      <div className="empty-state">
        <i className="mdi mdi-account-group-outline"></i>
        <p>No unassigned conversations</p>
      </div>
    );
  }

  return collections.map((user) => {
    const isSelected = selectedEmail === user.email;
    const isLoading = loadingCollection === user.email;
    const isActive = isUserActive(user);
    const unreadCount = unreadCounts[user.email] || 0;

    return (
      <div
        key={user.email}
        className={`user-item ${isSelected ? "selected" : ""} ${
          isLoading ? "loading" : ""
        } ${unreadCount > 0 ? "has-unread" : ""}`}
        onClick={() => onSelect(user.email)}
        role="button"
        tabIndex={0}
      >
        <div className="user-avatar-container">
          <div className={`user-avatar ${isActive ? "active" : ""}`}>
            {user.email.charAt(0).toUpperCase()}
            {/* Active Status Badge */}
            {isActive && (
              <div className="user-active-indicator">
                <div className="active-pulse"></div>
              </div>
            )}
          </div>

          {/* Loading Spinner */}
          {isLoading && (
            <div className="user-loading-overlay">
              <div className="loading-spinner">
                <div className="spinner-circle"></div>
              </div>
            </div>
          )}
        </div>
        <div className="user-info">
          <div className="user-header">
            <div className="user-identity">
              <span className="user-email">{user.email}</span>
              {isActive && (
                <span className="user-active-badge">
                  <span className="active-dot"></span>
                  Active now
                </span>
              )}
            </div>
          </div>
          {/* Enhanced Unread Badge */}
          {unreadCount > 0 && (
            <div className="unread-indicator">
              <span className="unread-count">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            </div>
          )}
        </div>
        <i className="mdi mdi-chevron-right user-arrow"></i>
      </div>
    );
  });
};

// ========== Enhanced ChatMessages with Seen Status ==========
const ChatMessages = ({ messages, loading, isTyping }) => {
  if (loading) {
    return (
      <div className="chat-loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Loading messages...</p>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="empty-chat-state">
        <i className="mdi mdi-message-text-outline"></i>
        <p>No messages yet</p>
        <small>Start a conversation with this user</small>
      </div>
    );
  }

  return (
    <>
      {messages.map((msg, idx) => {
        const isDateChanged =
          idx === 0 ||
          formatDateOnly(msg.time) !== formatDateOnly(messages[idx - 1]?.time);

        // ⭐ Check if message is read (admin messages that user has seen)
        const isRead = msg.read || msg.raw?.read || false;
        const messageKey = msg._id || `${msg.time}-${idx}`;

        return (
          <React.Fragment key={messageKey}>
            {isDateChanged && (
              <div className="chat-date-divider">
                <span>{formatDateOnly(msg.time)}</span>
              </div>
            )}
            <div
              className={`message-container ${
                msg.admin ? "outgoing" : "incoming"
              }`}
            >
              <div className="message-bubble">
                <div className="message-content">{msg.text}</div>
                <div className="message-meta">
                  <span className="message-time">
                    {formatTimeOnly(msg.time)}
                  </span>
                  {/* ⭐ ADD SEEN STATUS FOR ADMIN MESSAGES */}
                  {msg.admin && (
                    <span
                      className={`message-seen-status ${
                        isRead ? "seen" : "delivered"
                      }`}
                    >
                      {isRead ? "✓✓" : "✓"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      
      {/* Typing Indicator */}
      {isTyping && (
        <div className="typing-indicator">
          <div className="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span>User is typing...</span>
        </div>
      )}
    </>
  );
};

const ChatActionBar = ({
  selectedCollection,
  onStartConversation,
  loading,
}) => {
  if (!selectedCollection || loading) return null;

  return (
    <div className="chat-action-bar">
      <button className="start-conversation-btn" onClick={onStartConversation}>
        <i className="mdi mdi-message-plus" /> Start Conversation
      </button>
    </div>
  );
};

// ========== Socket Hook ==========
const useSocket = () => {
  const [socket, setSocket] = useState(null);
  const shopUrl = localStorage.getItem("shopURL");

  useEffect(() => {
    const url =
      process.env.REACT_APP_CHAT_BACKEND_SOCKET_URL ||
      process.env.REACT_APP_CHAT_BACKEND_URL;

    const s = io(url, { transports: ["websocket"] });

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      s.emit("registerShop", shopUrl);
    });

    s.on("registeredShop", (d) => console.log("Shop registered:", d));
    s.on("socketError", (e) => console.error("Socket error:", e));

    setSocket(s);

    return () => {
      s.disconnect();
      setSocket(null);
    };
  }, [shopUrl]);

  return socket;
};

// --- Main component ---
export default function UnassignedNew() {
  const navigate = useNavigate();
  const { collection } = useParams();
  const tenantName = localStorage.getItem("tenantName");

  // Enhanced hooks with all features
  const { collections, loading: chatLoading, unreadCounts, updateUnreadCount, refetch } = useChatData();
  const socket = useSocket();
  
  const [selectedCollection, setSelectedCollection] = useState("");
  const [loadingCollection, setLoadingCollection] = useState("");

  // Enhanced scroll management
  const { 
    chatContainerRef, 
    handleScroll, 
    scrollToBottomIfNeeded 
  } = useScrollManagement();
  
  // Enhanced chat messages with all features
  const {
    messages,
    userDetails,
    messagesLoading,
    setMessagesLoading,
    isTyping,
  } = useChatMessages(
    socket,
    selectedCollection,
    updateUnreadCount,
    scrollToBottomIfNeeded
  );

  // Handle selecting a collection
  const handleSelectCollection = useCallback((email) => {
    
    if (selectedCollection === email) {
      console.log("⏸️ Same collection clicked, skipping...");
      return;
    }
    
    setSelectedCollection(email);
      setMessagesLoading(true);
    setLoadingCollection(email);
  }, [selectedCollection]);

    useEffect(() => {
      if (!messagesLoading && loadingCollection) {
        setLoadingCollection("");
      }
    }, [messagesLoading, loadingCollection]);

  // Handle when URL param directly loads a collection
  useEffect(() => {
    if (collection && collection !== ":collection" && collection !== selectedCollection) {
      setSelectedCollection(collection);
      setMessagesLoading(true);
      setLoadingCollection(collection);
    }
  }, [collection, selectedCollection]);

  // 3) Start conversation button -> update status via API then navigate
  const handleStatusChange = useCallback(async () => {
    if (!selectedCollection) return;
    const localURL = localStorage.getItem("shopURL");
    const headers = {
      "Content-Type": "application/json",
      "x-shop-url": localURL,
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/status-update`,
        { email: selectedCollection, status: CHAT_STATUS.MY_OPEN },
        { headers }
      );
      navigate(`/${tenantName}/chat/Conversation/MyOpen/${selectedCollection}`);
    } catch (err) {
      console.error("Error updating status:", err);
    }
  }, [selectedCollection, tenantName, navigate]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      console.log("📜 Messages updated, count:", messages.length);
      scrollToBottomIfNeeded();
    }
  }, [messages, scrollToBottomIfNeeded]);

  if (chatLoading) {
    return (
      <div className="d-flex flex-column w-100">
        <Navbar />
        <div className="d-flex w-100">
          <NewSidebar />
          <div
            className="main-panel"
            style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
          >
            <div className="loading-container">
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column w-100">
      <Navbar />
      <div className="d-flex w-100">
        <NewSidebar />
        <div
          className="main-panel"
          style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
        >
          <div className="content-wrapper">
            <div className="d-xl-flex justify-content-between align-items-start">
              <h2 className="text-dark font-weight-bold mb-2">Conversation</h2>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="tab-content tab-transparent-content">
                  <div
                    className="tab-pane fade show active"
                    id="business-1"
                    role="tabpanel"
                  >
                    <div className="row">
                      <div className="col-12 grid-margin">
                        <div className="card">
                          <div className="card-body">
                            <div className="row d-flex flex-wrap">
                              <InboxPanel />

                              {/* Unassigned Users Panel */}
                              <div className="unassigned-users-panel col-md-3">
                                <div className="panel-header">
                                  <h3 className="panel-title">
                                    Unassigned Conversations
                                  </h3>
                                  <div className="panel-badge">
                                    {collections.length}
                                  </div>
                                </div>
                                <div className="users-list-container">
                                  <UnassignedUsersList
                                    collections={collections}
                                    tenantName={tenantName}
                                    onSelect={handleSelectCollection}
                                    selectedEmail={selectedCollection}
                                    loadingCollection={loadingCollection}
                                    unreadCounts={unreadCounts}
                                  />
                                </div>
                              </div>

                              {/* Chat Panel */}
                              <div className="unassigned-chat-panel col-md-4">
                                <div className="chat-panel-header">
                                  <h3 className="chat-title">
                                    {selectedCollection
                                      ? `Conversation: ${selectedCollection}`
                                      : "Unassigned Conversation"}
                                  </h3>
                                </div>

                                <div
                                  className={`chat-messages-container ${
                                    messagesLoading ? "loading" : ""
                                  }`}
                                  ref={chatContainerRef}
                                  onScroll={handleScroll}
                                >
                                  <ChatMessages
                                    messages={messages}
                                    loading={messagesLoading}
                                    isTyping={isTyping}
                                  />
                                </div>

                                <ChatActionBar
                                  selectedCollection={selectedCollection}
                                  onStartConversation={handleStatusChange}
                                  loading={messagesLoading}
                                />
                              </div>

                              {/* Information Panel */}
                              <div className="col-md-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h4 className="card-title mb-0">
                                    Information
                                  </h4>
                                </div>
                                <div className="info-panel">
                                  <UserDetails {...userDetails} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// import React, { useEffect, useRef, useState, useCallback } from "react";
// import { Link, useNavigate, useParams } from "react-router-dom";
// import { DateTime } from "luxon";
// import axios from "axios";
// import { Spinner } from "react-bootstrap";
// import Navbar from "../NavBar";
// import NewSidebar from "../newSideBar";
// import UserDetails from "./UserDetails";
// import InboxPanel from "./InboxPanel";
// import "./inbox.css";

// // Constants and configuration
// const CHAT_STATUS = {
//   UNASSIGNED: 1,
//   MY_OPEN: 2
// };

// const POLLING_INTERVAL = 1000;
// const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

// // Custom hooks for better separation of concerns
// const useChatData = () => {
//   const [collections, setCollections] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const localURL = localStorage.getItem("shopURL");

//   const headers = {
//     "Content-Type": "application/json",
//     "x-shop-url": localURL,
//   };

//   const fetchCollections = useCallback(async () => {
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/email-list/${CHAT_STATUS.UNASSIGNED}`,
//         { headers }
//       );
//       setCollections(response.data.users || []);
//     } catch (error) {
//       console.error("Error fetching collections:", error);
//       setCollections([]);
//     } finally {
//       setLoading(false);
//     }
//   }, [headers]);

//   useEffect(() => {
//     fetchCollections();
//   }, [fetchCollections]);

//   return { collections, loading, refetch: fetchCollections };
// };

// const useChatMessages = (selectedCollection) => {
//   const [messages, setMessages] = useState([]);
//   const [userDetails, setUserDetails] = useState({
//     email: "",
//     system: "",
//     ip: "",
//     location: ""
//   });

//   const localURL = localStorage.getItem("shopURL");

//   const headers = {
//     "Content-Type": "application/json",
//     "x-shop-url": localURL,
//   };

//   const fetchMessages = useCallback(async (collection) => {
//     if (!collection) return;

//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/email-click/${collection}`,
//         { headers }
//       );

//       const { info, user_message } = response.data;

//       // Sort messages by time
//       const sortedMessages = (user_message || []).sort((a, b) => {
//         const timeA = DateTime.fromFormat(a.time, "dd-MM-yyyy h:mm a");
//         const timeB = DateTime.fromFormat(b.time, "dd-MM-yyyy h:mm a");
//         return timeA - timeB;
//       });

//       // Transform messages to consistent format
//       const formattedMessages = sortedMessages.map(msg => ({
//         text: msg.message,
//         admin: false,
//         time: msg.time
//       }));

//       setMessages(formattedMessages);
//       setUserDetails({
//         email: info?.email || "",
//         system: info?.system || "",
//         ip: info?.ip || "",
//         location: info?.location || ""
//       });
//     } catch (error) {
//       console.error(`Error fetching messages for ${collection}:`, error);
//       setMessages([]);
//     }
//   }, [headers]);

//   return { messages, userDetails, fetchMessages };
// };

// const useScrollManagement = () => {
//   const chatContainerRef = useRef(null);
//   const chatEndRef = useRef(null);
//   const [isAtBottom, setIsAtBottom] = useState(true);

//   const handleScroll = useCallback(() => {
//     if (!chatContainerRef.current) return;

//     const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
//     setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
//   }, []);

//   const scrollToBottom = useCallback(() => {
//     if (isAtBottom && chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [isAtBottom]);

//   return {
//     chatContainerRef,
//     chatEndRef,
//     isAtBottom,
//     handleScroll,
//     scrollToBottom
//   };
// };

// // Utility functions
// const formatDateOnly = (dateString) => {
//   const [datePart] = dateString.split(" ");
//   const [day, month, year] = datePart.split("-").map(Number);
//   return `${day.toString().padStart(2, "0")}-${month.toString().padStart(2, "0")}-${year}`;
// };

// const formatTimeOnly = (timestamp) => {
//   const parts = timestamp.split(' ');
//   const timeParts = parts[1].split(':');
//   return `${timeParts[0]}:${timeParts[1]} ${parts[2]}`;
// };

// const isUserActive = (user) => {
//   if (!user?.updatedAt) return false;
//   return new Date(user.updatedAt).getTime() >= (Date.now() - INACTIVITY_THRESHOLD);
// };

// // Sub-components for better organization
// const UnassignedUsersList = ({ collections, tenantName }) => {
//   if (collections.length === 0) {
//     return (
//       <div className="empty-state">
//         <i className="mdi mdi-account-group-outline"></i>
//         <p>No unassigned conversations</p>
//       </div>
//     );
//   }

//   return collections.map((user, index) => (
//     <Link
//       key={user.email}
//       to={`/${tenantName}/chat/Conversation/Unassigned/${user.email}`}
//       className="user-item"
//     >
//       <div className={`user-avatar ${isUserActive(user) ? "active" : ""}`}>
//         {user.email.charAt(0).toUpperCase()}
//       </div>
//       <div className="user-info">
//         <span className="user-email">{user.email}</span>
//         {isUserActive(user) && (
//           <span className="user-status">Active now</span>
//         )}
//       </div>
//       <i className="mdi mdi-chevron-right user-arrow"></i>
//     </Link>
//   ));
// };

// const ChatMessages = ({ messages, chatContainerRef, handleScroll }) => {
//   if (messages.length === 0) {
//     return (
//       <div className="empty-chat-state">
//         <i className="mdi mdi-message-text-outline"></i>
//         <p>No messages yet</p>
//         <small>Start a conversation with this user</small>
//       </div>
//     );
//   }

//   return (
//     <>
//       <div className="chat-date-divider">
//         <span>{formatDateOnly(messages[0].time)}</span>
//       </div>

//       {messages.map((msg, index) => (
//         <div
//           key={`${msg.time}-${index}`}
//           className={`message-container ${msg.admin ? "outgoing" : "incoming"}`}
//         >
//           <div className="message-bubble">
//             <div className="message-content">{msg.text}</div>
//             <div className="message-time">{formatTimeOnly(msg.time)}</div>
//           </div>
//         </div>
//       ))}
//     </>
//   );
// };

// const ChatActionBar = ({ selectedCollection, onStartConversation }) => {
//   if (!selectedCollection) return null;

//   return (
//     <div className="chat-action-bar">
//       <button className="start-conversation-btn" onClick={onStartConversation}>
//         <i className="mdi mdi-message-plus"></i>
//         Start Conversation
//       </button>
//     </div>
//   );
// };

// // Main component
// function UnassignedNew() {
//   const navigate = useNavigate();
//   const { collection } = useParams();
//   const tenantName = localStorage.getItem("tenantName");

//   const [selectedCollection, setSelectedCollection] = useState("");

//   const { collections, loading: chatLoading } = useChatData();
//   const { messages, userDetails, fetchMessages } = useChatMessages(selectedCollection);
//   const { chatContainerRef, chatEndRef, handleScroll, scrollToBottom } = useScrollManagement();

//   // Handle collection selection
//   const handleCollectionClick = useCallback((collection) => {
//     setSelectedCollection(collection);
//     fetchMessages(collection);
//   }, [fetchMessages]);

//   // Handle status change and navigation
//   const handleStatusChange = useCallback(async () => {
//     if (!selectedCollection) return;

//     const requestData = {
//       email: selectedCollection,
//       status: CHAT_STATUS.MY_OPEN
//     };

//     try {
//       const localURL = localStorage.getItem("shopURL");
//       const headers = {
//         "Content-Type": "application/json",
//         "x-shop-url": localURL,
//       };

//       await axios.post(
//         `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/status-update`,
//         requestData,
//         { headers }
//       );

//       navigate(`/${tenantName}/chat/Conversation/MyOpen/${selectedCollection}`);
//     } catch (error) {
//       console.error("Error updating status:", error);
//     }
//   }, [selectedCollection, tenantName, navigate]);

//   // Effects for data synchronization
//   useEffect(() => {
//     if (collection && collection !== ":collection") {
//       handleCollectionClick(collection);
//     }
//   }, [collection, handleCollectionClick]);

//   useEffect(() => {
//     scrollToBottom();
//   }, [messages, scrollToBottom]);

//   useEffect(() => {
//     if (!selectedCollection) return;

//     const interval = setInterval(() => {
//       fetchMessages(selectedCollection);
//     }, POLLING_INTERVAL);

//     return () => clearInterval(interval);
//   }, [selectedCollection, fetchMessages]);

//   if (chatLoading) {
//     return (
//       <div className="d-flex flex-column w-100">
//         <Navbar />
//         <div className="d-flex w-100">
//           <NewSidebar />
//           <div
//             className="main-panel"
//             style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
//           >
//             <div className="loading-container">
//               <Spinner
//                 animation="border"
//                 variant="primary"
//                 style={{ width: "3rem", height: "3rem" }}
//               />
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="d-flex flex-column w-100">
//       <Navbar />
//       <div className="d-flex w-100">
//         <NewSidebar />
//         <div
//           className="main-panel"
//           style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
//         >
//           <div className="content-wrapper">
//             <div className="d-xl-flex justify-content-between align-items-start">
//               <h2 className="text-dark font-weight-bold mb-2">Conversation</h2>
//             </div>

//             <div className="row">
//               <div className="col-md-12">
//                 <div className="tab-content tab-transparent-content">
//                   <div
//                     className="tab-pane fade show active"
//                     id="business-1"
//                     role="tabpanel"
//                     aria-labelledby="business-tab"
//                   >
//                     <div className="row">
//                       <div className="col-12 grid-margin">
//                         <div className="card">
//                           <div className="card-body">
//                             <div className="row d-flex flex-wrap">
//                               <InboxPanel />

//                               {/* Unassigned Users Panel */}
//                               <div className="unassigned-users-panel col-md-3">
//                                 <div className="panel-header">
//                                   <h3 className="panel-title">Unassigned Conversations</h3>
//                                   <div className="panel-badge">{collections.length}</div>
//                                 </div>

//                                 <div className="users-list-container">
//                                   <UnassignedUsersList
//                                     collections={collections}
//                                     tenantName={tenantName}
//                                   />
//                                 </div>
//                               </div>

//                               {/* Chat Panel */}
//                               <div className="unassigned-chat-panel col-md-4">
//                                 <div className="chat-panel-header">
//                                   <h3 className="chat-title">Unassigned Conversation</h3>
//                                 </div>

//                                 <div
//                                   className="chat-messages-container"
//                                   ref={chatContainerRef}
//                                   onScroll={handleScroll}
//                                 >
//                                   <ChatMessages
//                                     messages={messages}
//                                     chatContainerRef={chatContainerRef}
//                                     handleScroll={handleScroll}
//                                   />
//                                   <div ref={chatEndRef} />
//                                 </div>

//                                 <ChatActionBar
//                                   selectedCollection={selectedCollection}
//                                   onStartConversation={handleStatusChange}
//                                 />
//                               </div>

//                               {/* Information Panel */}
//                               <div className="col-md-3">
//                                 <div className="d-flex justify-content-between align-items-center mb-2">
//                                   <h4 className="card-title mb-0">Information</h4>
//                                 </div>
//                                 <div className="info-panel">
//                                   <UserDetails {...userDetails} />
//                                 </div>
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default UnassignedNew;
